import type { TFunction } from "i18next";
import {
  ContractNature,
  FoaPart,
  ProcedureType,
  Regulation,
  ServiceCategory,
} from "../../autogen/bff-api";
import { offersDesignContest } from "./designContest";
import { isAnnouncedPartI, isPartIHealthSocialEducationService } from "./foaPartDisplay";
import { isFoaPartIV } from "./foaPartII";
import { isAtOrAboveEea, minRequestDays, minTenderDays, tenderDeadlineProvision } from "./karens";
import { offersWithoutCompetition } from "./noCompetition";
import { isLeftToTheBuyer, isTwoStageProcedure, runsBelowEeaThreshold } from "./procedure";
import {
  PROCEDURE_TYPE_OPTIONS,
  procedureProvisionKey,
  procedureTypeOptionsFor,
  type ProcedureTypeOption,
} from "./publicProcurementOptions";

export type ProcedureInputs = {
  regulation: Regulation | null | undefined;
  foaPart: FoaPart | null | undefined;
  contractNature: ContractNature | null | undefined;
  serviceCategory: ServiceCategory | null | undefined;
  estimatedValueNok: number | null | undefined;
  announcedOnDoffin?: boolean | null;
};

export type ProcedureAvailability = {
  options: readonly ProcedureTypeOption[];
  partIHealthSocialEducation: boolean;
  overEeaThreshold: boolean;
  partIV: boolean;
  underUtilities: boolean;
  offersTwoStage: boolean;
  offersWithoutCompetition: boolean;
  offersDesignContest: boolean;
  meetsPartRequirement: (procedureType: ProcedureType) => boolean;
};

const ANNOUNCED_PART_I_OPTIONS = PROCEDURE_TYPE_OPTIONS.filter((option) => option.value === ProcedureType.Open);

export const procedureAvailability = (inputs: ProcedureInputs): ProcedureAvailability => {
  const { regulation, foaPart } = inputs;
  const partIHealthSocialEducation = isPartIHealthSocialEducationService(
    regulation,
    foaPart,
    inputs.contractNature,
    inputs.serviceCategory,
  );
  const overEeaThreshold = isAtOrAboveEea(regulation, foaPart);
  const partIV = isFoaPartIV(regulation, foaPart);
  const announcedPartI = isAnnouncedPartI({ regulation, foaPart, announcedOnDoffin: inputs.announcedOnDoffin });
  if (announcedPartI) {
    return {
      options: ANNOUNCED_PART_I_OPTIONS,
      partIHealthSocialEducation,
      overEeaThreshold,
      partIV,
      underUtilities: false,
      offersTwoStage: false,
      offersWithoutCompetition: false,
      offersDesignContest: false,
      meetsPartRequirement: (procedureType) => procedureType === ProcedureType.Open,
    };
  }
  const offersTwoStage = (overEeaThreshold && !partIV) || partIHealthSocialEducation;
  return {
    options: procedureTypeOptionsFor(regulation, foaPart, partIHealthSocialEducation),
    partIHealthSocialEducation,
    overEeaThreshold,
    partIV,
    underUtilities: regulation === Regulation.Forsyningsforskriften,
    offersTwoStage,
    offersWithoutCompetition: offersWithoutCompetition(regulation, foaPart),
    offersDesignContest: offersDesignContest(
      regulation,
      foaPart,
      inputs.estimatedValueNok,
      partIHealthSocialEducation,
    ),
    meetsPartRequirement: (procedureType) =>
      !isTwoStageProcedure(procedureType) ||
      offersTwoStage ||
      runsBelowEeaThreshold(procedureType) ||
      isLeftToTheBuyer(regulation, foaPart, procedureType),
  };
};

export const procedureLegalBasis = (
  procedureType: ProcedureType,
  inputs: ProcedureInputs,
  t: TFunction,
): string | null => {
  const underUtilities = inputs.regulation === Regulation.Forsyningsforskriften;
  switch (procedureType) {
    case ProcedureType.QualificationScheme:
      return t("Announced once and open for its whole validity — forsyningsforskriften § 13-1");
    case ProcedureType.DynamicPurchasingSystem:
      return underUtilities
        ? t("Open to every qualified supplier for its whole validity — forsyningsforskriften §§ 22-2 to 22-5")
        : null;
    case ProcedureType.NegotiatedWithoutPriorCall:
      return underUtilities ? "forsyningsforskriften § 9-2" : "FOA § 13-3";
    case ProcedureType.DesignContestOpen:
    case ProcedureType.DesignContestRestricted:
      return underUtilities ? "forsyningsforskriften kap. 27" : "FOA kap. 31";
    case ProcedureType.WithoutCompetition:
      return underUtilities ? t("forsyningsforskriften § 9-3") : t("FOA § 13-4");
    default: {
      const key = procedureProvisionKey(
        inputs.regulation,
        procedureType,
        inputs.foaPart,
        procedureAvailability(inputs).partIHealthSocialEducation,
      );
      return key ? t(key) : null;
    }
  }
};

export type AllowedProcedure = {
  procedureType: ProcedureType;
  legalBasis: string | null;
  minimumTenderDays: number | null;
  tenderDeadlineLegalBasis: string | null;
  minimumRequestDays: number | null;
};

export const allowedProcedures = (inputs: ProcedureInputs, t: TFunction): AllowedProcedure[] =>
  offeredProcedures(inputs).map((procedureType) => ({
    procedureType,
    legalBasis: procedureLegalBasis(procedureType, inputs, t),
    minimumTenderDays: minTenderDays(inputs.regulation, inputs.foaPart, procedureType),
    tenderDeadlineLegalBasis: tenderDeadlineProvision(inputs.regulation, inputs.foaPart, procedureType),
    minimumRequestDays: minRequestDays(inputs.regulation, inputs.foaPart, procedureType),
  }));

export const offeredProcedures = (inputs: ProcedureInputs): ProcedureType[] => {
  const availability = procedureAvailability(inputs);
  return [
    ...availability.options
      .filter((option) => option.enabled && availability.meetsPartRequirement(option.value))
      .map((option) => option.value),
    ...(availability.underUtilities && availability.overEeaThreshold ? [ProcedureType.QualificationScheme] : []),
    ...(availability.offersTwoStage ? [ProcedureType.DynamicPurchasingSystem] : []),
    ...(availability.offersWithoutCompetition ? [ProcedureType.NegotiatedWithoutPriorCall] : []),
    ...(availability.offersDesignContest ? [ProcedureType.DesignContestOpen] : []),
    ...(availability.offersWithoutCompetition ? [ProcedureType.WithoutCompetition] : []),
  ];
};
